import { Card } from '@guridosdrones/ui';
import { motion } from 'framer-motion';
import {
  BookOpen,
  ClipboardCheck,
  CloudSun,
  Cog,
  Droplets,
  Layers,
  Map as MapIcon,
  Plane,
} from 'lucide-react';
import { FadeIn } from '@/components';

const modules = {
  basic: [
    {
      title: 'Estrutura e Mecânica',
      description: 'Domine o funcionamento mecânico e eletrônico. Entenda o coração do seu drone.',
      icon: Cog,
    },
    {
      title: 'Legislação e Normas',
      description:
        'Opere legalmente. Domine as regras da ANAC, DECEA e MAPA para voar com segurança jurídica.',
      icon: BookOpen,
    },
    {
      title: 'Meteorologia Aplicada',
      description:
        'Planeje operações precisas. Entenda como o clima afeta a deriva e a eficiência da aplicação.',
      icon: CloudSun,
    },
    {
      title: 'Manutenção Preventiva',
      description:
        'Prolongue a vida útil do equipamento. Checklists diários e cuidados essenciais.',
      icon: ClipboardCheck,
    },
  ],
  advanced: [
    {
      title: 'Pilotagem Profissional',
      description:
        'Manuseio prático em cenários reais. Técnicas para desvio de obstáculos e voo em declive.',
      icon: Plane,
    },
    {
      title: 'Tecnologia de Aplicação',
      description: 'Mistura de calda, bicos e dosagem correta para máxima eficiência agronômica.',
      icon: Droplets,
    },
    {
      title: 'Mapeamento 3D',
      description:
        'Metodologia exclusiva de acompanhamento de relevo para aplicação perfeita em qualquer terreno.',
      icon: MapIcon,
    },
    {
      title: 'Gestão de Frota',
      description:
        'Escale seu negócio. Planejamento logístico, gerenciamento de equipe e manutenção de frota.',
      icon: Layers,
    },
  ],
};

export function CourseDetails() {
  return (
    <section
      id="course-details"
      className="section-shell bg-muted/30 transition-colors duration-300"
    >
      <div className="section-container">
        <div className="section-header">
          <FadeIn direction="up" distance={20}>
            <p className="mb-3 font-mono text-xs tracking-[0.25em] text-primary/70 uppercase">
              Módulos do Sistema
            </p>
            <h2 className="section-title mb-4">O Que Você Vai Aprender</h2>
            <p className="section-description">
              8 módulos práticos que cobrem da base técnica à operação avançada. Tudo o que você
              precisa para atuar com segurança e resultado no campo.
            </p>
          </FadeIn>
        </div>

        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-2 lg:gap-14">
          {/* Basic Module Column */}
          <div className="flex flex-col gap-7">
            <div className="mb-6 flex items-center gap-4">
              <div className="h-12 w-0.5 bg-primary/60" />
              <div>
                <p className="font-mono text-[10px] tracking-[0.2em] text-primary/50 uppercase">
                  SYS_01
                </p>
                <h3 className="text-2xl font-bold text-foreground">Fundamentos</h3>
                <p className="text-muted-foreground">A base sólida de todo piloto de elite</p>
              </div>
            </div>

            <div className="grid gap-5">
              {modules.basic.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="group flex items-start gap-5 border-border bg-card p-6 transition-all duration-300 hover:border-primary/35 hover:shadow-lg hover:shadow-primary/10">
                    <div className="border border-primary/20 bg-primary/8 p-3 text-primary transition-transform duration-300 group-hover:scale-110">
                      <item.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="mb-2 text-lg font-bold text-foreground transition-colors group-hover:text-primary">
                        {item.title}
                      </h4>
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        {item.description}
                      </p>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Advanced Module Column */}
          <div className="flex flex-col gap-7">
            <div className="mb-6 flex items-center gap-4">
              <div className="h-12 w-0.5 bg-secondary/60" />
              <div>
                <p className="font-mono text-[10px] tracking-[0.2em] text-secondary/50 uppercase">
                  SYS_02
                </p>
                <h3 className="text-2xl font-bold text-foreground">Especialização</h3>
                <p className="text-muted-foreground">Técnicas avançadas para alta performance</p>
              </div>
            </div>

            <div className="grid gap-5">
              {modules.advanced.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="group flex items-start gap-5 border-border bg-card p-6 transition-all duration-300 hover:border-secondary/35 hover:shadow-lg hover:shadow-secondary/10">
                    <div className="border border-secondary/20 bg-secondary/10 p-3 text-secondary transition-transform duration-300 group-hover:scale-110">
                      <item.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="mb-2 text-lg font-bold text-foreground transition-colors group-hover:text-secondary">
                        {item.title}
                      </h4>
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        {item.description}
                      </p>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
